import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import 'highcharts/modules/no-data-to-display'
import { HIGHCHART_SERIES_COLORS } from 'constants/colorMaps'
import { useTranslation } from 'react-i18next'

import StyledHighcharts, { tooltipStyle } from './StyledHighcharts'

import moment from 'moment-wrapper'

// modifies chartOptions and returns it
const addThreshold = (chartOptions, threshold, thresholdCondition, thresholdLabelFunc) => {
  const THRESHOLD_COLOR = '#E3814033'
  const lessThan = thresholdCondition === 'lt'
  const color = lessThan ? 'transparent' : THRESHOLD_COLOR
  const negativeColor = lessThan ? THRESHOLD_COLOR : 'transparent'

  chartOptions.chart.type = 'areaspline'
  chartOptions.plotOptions.series.threshold = threshold
  chartOptions.plotOptions.series.fillColor = color
  chartOptions.plotOptions.series.negativeFillColor = negativeColor

  chartOptions.yAxis.min = 0
  chartOptions.yAxis.plotLines = [{
    value: threshold,
    color: THRESHOLD_COLOR,
    dashStyle: 'shortdash',
    width: 2,
    zIndex: 5,
    label: {
      text: thresholdLabelFunc(threshold, thresholdCondition),
    },
  }]

  return chartOptions
}

const makeChartOptions = (config, data, t, thresholdLabelFunc) => {
  const { type, displayTimezone, timeFormat24 } = config

  const noDataText = t('common.noChartData')
  let chartOptions = {
    colors: HIGHCHART_SERIES_COLORS,

    chart: {
      type: type,
      spacing: [14, 32, 20, 32],
      style: {
        fontSize: '16px',
      },
    },

    lang: {
      noData:
        '<div style="text-align: center; position: relative;">' +
          '<i class="icon-chart-nodata -nohover" style="margin-bottom: 16px;"></i><br>' +
          noDataText +
        '</div>',
    },
    noData: {
      style: {
        fontWeight: 'normal',
        fontSize: '12px',
        fontFamily: 'Open Sans',
      },
      useHTML: true,
    },

    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        hour: timeFormat24 ? '%H:%M' : '%l %p',
      },
      tickLength: 0,
      minPadding: 0,
      maxPadding: 0,
      crosshair: {
        color: '#CEE2F1',
      },
      showEmpty: false,
      labels: {
        style: {
          fontSize: '11px',
        },
      },
    },

    yAxis: {
      title: {
        text: null,
      },
      endOnTick: false, // there was too much space above the chart without this
      plotLines: [],
      min: 0,
      labels: {
        style: {
          fontSize: '11px',
        },
      },
    },

    tooltip: {
      ...tooltipStyle,
      outside: true,
      shared: true,
      formatter: function (tooltip) {
        const date = Highcharts.dateFormat('%d %m %Y %H:%M', this.x)
        const str = `${moment(date, 'DD MM YYYY HH:mm').format('LLL')} ${displayTimezone}`

        return this.points.reduce(function (s, point) {
          return `${s}<br/> <span style="color: ${point.series.color}">${point.series.name}</span>: <b>${point.y}</b>`
        }, str)
      },
    },

    plotOptions: {
      series: {
        fillOpacity: 0.3,
        stacking: 'normal',
        marker: {
          enabled: false,
          symbol: 'circle',
        },
        connectNulls: true,
      },
    },

    legend: {
      enabled: true,
      align: 'left',
      x: 28,
      itemDistance: 12,
      margin: 10,
      padding: 3,
      symbolPadding: 5,
      backgroundColor: '#f1f1f1',
      borderRadius: 6,
      borderColor: '#f1f1f1',
      borderWidth: 4,
      itemStyle: {
        fontSize: '10px',
        fontWeight: 'normal',
        fontFamily: 'Open Sans',
      },
    },
    credits: { enabled: false },
    title: { text: undefined },
    accessibility: { enabled: false },
  }

  if (data) {
    chartOptions.series = data
  }

  const hasThreshold = data && data.length === 1 && data[0].options
  if (hasThreshold) {
    const { threshold, thresholdCondition } = data[0].options
    chartOptions = addThreshold(chartOptions, threshold, thresholdCondition, thresholdLabelFunc)
  }

  return chartOptions
}

const Chart = ({ config, data, thresholdLabelFunc }) => {
  const { t } = useTranslation()
  const chartRef = useRef(null)
  const [chartOptions, setChartOptions] = useState(makeChartOptions(config, data, t, thresholdLabelFunc))

  useEffect(() => {
    updateConfigAndSeries()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const chart = chartRef.current?.chart
    updateSeries()
    chart?.reflow()
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateConfigAndSeries()
  }, [config]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateConfigAndSeries = () => {
    const chartOptions = makeChartOptions(config, data, t, thresholdLabelFunc)
    setChartOptions(chartOptions)
  }

  const updateSeries = () => {
    setChartOptions({
      ...chartOptions,
      ...{
        series: data,
      },
    })
  }

  return (
    <StyledHighcharts
      highcharts={Highcharts}
      options={chartOptions}
    />
  )
}

Chart.propTypes = {
  config: PropTypes.shape({
    type: PropTypes.string.isRequired,
    displayTimezone: PropTypes.string.isRequired,
    timeFormat24: PropTypes.bool,
  }),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      color: PropTypes.string,
      pointStart: PropTypes.number.isRequired,
      pointInterval: PropTypes.number.isRequired,
      threshold: PropTypes.number,
      thresholdCondition: PropTypes.string,
      data: PropTypes.array.isRequired,
    })
  ),
  thresholdLabelFunc: PropTypes.func,
}

export default Chart
